import {extend} from 'flarum/common/extend';
import ComposerState from 'flarum/forum/states/ComposerState';
import ReplyComposer from 'flarum/forum/components/ReplyComposer';
import EditPostComposer from 'flarum/forum/components/EditPostComposer';
import Discussion from 'flarum/common/models/Discussion';
import Post from 'flarum/common/models/Post';
import Stream from 'flarum/common/utils/Stream';
import Uploader from './states/Uploader';
import ComposerAttachments from './components/ComposerAttachments';

interface PostComposer {
    attrs: {
        discussion?: Discussion
        post?: Post
    }
    composer: ComposerState & {
        fields: {
            content: Stream<string>
            cimaiseExcludedPictures: string[]
        }
    }
    cimaiseUploader?: Uploader
}

function extendComposer(Composer: any) {
    extend(Composer.prototype, 'oninit' as any, function (this: PostComposer) {
        const discussion = this.attrs.discussion || this.attrs.post?.discussion();

        if (!discussion) {
            return;
        }

        const album = discussion.cimaiseAlbum();

        if (!album || !album.canUpload()) {
            return;
        }

        // Store the list of **un**selected pictures as it's much easier to keep in sync as pictures are uploaded and deleted
        this.composer.fields.cimaiseExcludedPictures = [];

        this.cimaiseUploader = new Uploader(album.id()!);
        this.cimaiseUploader.loadExistingDrafts();
    });

    // Flarum doesn't have a way to insert below the TextEditor in the composer, so we'll hook into the view directly
    extend(Composer.prototype, 'view', function (this: PostComposer, vdom) {
        if (!this.cimaiseUploader) {
            return;
        }

        // Fallback in case we can't find any other location
        let target = vdom;

        // vdom is ConfirmDocumentUnload
        vdom.children.forEach((vnode: any) => {
            if (!vnode || !vnode.children) {
                return;
            }
            // vnode is .ComposerBody
            if (vnode.attrs && vnode.attrs.className) {
                vnode.attrs.className += ' ComposerBody--cimaise-attachments';
            }

            vnode.children.forEach((vnode: any) => {
                if (vnode && vnode.attrs && vnode.attrs.className === 'ComposerBody-content') {
                    target = vnode;
                }
            });
        });

        target.children.push(m(ComposerAttachments, {
            uploader: this.cimaiseUploader,
            composer: this.composer,
        }));
    });

    extend(Composer.prototype, 'data' as any, function (this: PostComposer, data: any) {
        if (!this.cimaiseUploader) {
            return;
        }

        const pictures = this.cimaiseUploader.pictures.filter(upload => {
            return upload.picture && this.composer.fields.cimaiseExcludedPictures.indexOf(upload.picture.id()!) === -1;
        }).map(upload => upload.picture!);

        if (pictures.length === 0) {
            return;
        }

        data.relationships.cimaisePictures = pictures;
    });
}

export default function () {
    extendComposer(ReplyComposer);
    extendComposer(EditPostComposer);
}
